/* Currency symbols for $-currencies. Replaces currency-symbol-replace.js and the inline price reformatter that
   layout/theme.liquid used to carry, with the same rules and the same timing:
   - price rule (all currencies; on DOMContentLoaded, on shopify:section:load/reorder and for DOM changes during the
     first 10 s): direct text of .price, .price-item, [class*="price"], [data-price] that contains "$" loses "USD",
     has its whitespace collapsed and, for CAD/AUD/NZD/HKD/SGD, gets the prefixed symbol (CA$, A$, NZ$, HK$, S$);
   - bare-amount rule (CAD/AUD/NZD only; on DOMContentLoaded and for every later DOM change): an element holding
     nothing but a "$59" / "$1,299.99" amount gets the same prefix.
   The old scripts re-scanned the whole document on every DOM change (and polled for Shopify.currency); here only
   the nodes a mutation touched are looked at. One deliberate difference: a text that already contains the prefix is
   never prefixed again (the old check looked only at the start, so "From CA$10.00" could become "From CACA$10.00"). */
(() => {
  if (window.hyCurrency) return;
  const PRICE_SELECTOR = '.price, .price-item, [class*="price"], [data-price]';
  const PREFIX = { CAD: 'CA$', AUD: 'A$', NZD: 'NZ$', HKD: 'HK$', SGD: 'S$' };
  const BARE_PREFIX = { CAD: 'CA$', AUD: 'A$', NZD: 'NZ$' };
  const BARE_AMOUNT = /^\$(\d{1,3}(?:,\d{3})*(?:\.\d{2})?|\d+(?:\.\d{2})?)$/;
  const PRICE_WINDOW_MS = 10000;

  const currency = () =>
    (window.Shopify && Shopify.currency && (Shopify.currency.active || Shopify.currency.currency)) ||
    document.documentElement.lang ||
    'USD';
  const bareSymbol = () => {
    const code = window.Shopify && Shopify.currency && Shopify.currency.active;
    return Object.prototype.hasOwnProperty.call(BARE_PREFIX, code) ? BARE_PREFIX[code] : null;
  };

  const formatPriceText = (text, code) => {
    if (!text.includes('$')) return text;
    text = text.replace(/\s?USD/g, '').replace(/\s+/g, ' ').trim();
    const prefix = PREFIX[code];
    // Idempotent: text that already carries the prefix anywhere ("From CA$10.00") is left alone, so a second pass
    // never produces "CACA$". (The old script only checked the start of the text.)
    if (prefix && !text.includes(prefix)) text = text.replace('$', prefix);
    return text;
  };

  const fixPriceElement = (el, code) => {
    el.childNodes.forEach((node) => {
      if (node.nodeType !== Node.TEXT_NODE) return;
      const formatted = formatPriceText(node.textContent, code);
      if (formatted !== node.textContent) node.textContent = formatted;
    });
  };

  const fixBareAmount = (el, symbol) => {
    if (el.childNodes.length !== 1 || el.children.length > 0) return;
    const text = el.textContent.trim();
    if (BARE_AMOUNT.test(text)) el.textContent = text.replace('$', symbol);
  };

  const priceRule = () => {
    const code = currency();
    document.querySelectorAll(PRICE_SELECTOR).forEach((el) => fixPriceElement(el, code));
  };
  const bareRule = () => {
    const symbol = bareSymbol();
    if (symbol) document.querySelectorAll('*').forEach((el) => fixBareAmount(el, symbol));
  };

  let windowOpen = true;
  setTimeout(() => { windowOpen = false; }, PRICE_WINDOW_MS);
  let bareActive = false; // like the old script, the bare-amount rule starts at DOMContentLoaded

  const onMutations = (records) => {
    const price = windowOpen;
    const symbol = bareActive ? bareSymbol() : null;
    if (!price && !symbol) return;
    const code = currency();
    const seen = new Set();
    const fix = (el) => {
      if (!el || el.nodeType !== Node.ELEMENT_NODE || seen.has(el)) return;
      seen.add(el);
      if (price && el.matches(PRICE_SELECTOR)) fixPriceElement(el, code);
      if (symbol) fixBareAmount(el, symbol);
    };
    const fixTree = (root) => {
      fix(root);
      if (price) root.querySelectorAll(PRICE_SELECTOR).forEach(fix);
      if (symbol) root.querySelectorAll('*').forEach(fix);
    };
    records.forEach((record) => {
      fix(record.target);
      record.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) fixTree(node);
        else fix(node.parentElement);
      });
    });
  };

  // Like the old scripts: element/child changes only. Text-node edits (including this script's own writes) are not
  // observed, so the observer cannot feed itself.
  new MutationObserver(onMutations).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'data-price'],
  });

  const start = () => {
    priceRule();
    bareActive = true;
    bareRule();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', priceRule);
  document.addEventListener('shopify:section:reorder', priceRule);

  window.hyCurrency = { priceRule, bareRule };
})();
